import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { getSubSearchParam } from '../../../../../utils/route'
import { useAsyncEntity } from '../../../../Entity'
import {
  getEmailMessages,
  getEmailProfileWidgetsInfo,
  getEmailThreadProfile,
  updateEmail,
} from '../../../Email.api'
import { EMAIL_PROFILE_WIDGET_NAME_AI_SUMMARY } from '../../../Email.constants'
import {
  useEmailMessageCompletedRealtime,
  useEmailProfileAISummaryData,
  useEmailProfileRead,
  useEmailProfileWidgetsData,
} from '../../../Email.hooks'
import { isEmailMessagePending } from '../../../Email.utils'
import {
  getEmailMessageProfileData,
  getEmailMessageProfileNextUpdatedData,
} from '../../EmailMessageProfile.utils'

export function useEmailMessageProfileLoaders({
  getSummaryData: getSummaryDataApi = getEmailProfileWidgetsInfo,
  isReadEnabled = true,
  mailUuid,
  withWidgets,
  onSuccessRead,
}) {
  const [searchParams] = useSearchParams()
  const sub = getSubSearchParam(searchParams)

  useEmailProfileRead({
    isEnabled: isReadEnabled,
    mailUuid,
    single: true,
    onSuccessRead,
  })

  const [realtimeChannel, setRealtimeChannel] = useState(null)

  async function loadAISummaryWidgetData(apiConfig) {
    return getSummaryDataApi(
      {
        sub_menu: sub,
        uuid: mailUuid,
        widget_name: EMAIL_PROFILE_WIDGET_NAME_AI_SUMMARY,
      },
      apiConfig
    )
  }

  async function getWidgetsData() {
    return getEmailProfileWidgetsInfo({
      sub_menu: sub,
      uuid: mailUuid,
    })
  }

  async function getProfileData(apiConfig = EMPTY_OBJECT) {
    return getEmailMessages({
      ...apiConfig,
      params: {
        sub_menu: sub,
        uuid: [mailUuid],
      },
    }).then(getEmailMessageProfileData)
  }

  async function updateProfileData(data) {
    return updateEmail(mailUuid, data, true)
  }

  const {
    entityData,
    handleEntityReload,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({
    getNextUpdatedEntityData: getEmailMessageProfileNextUpdatedData,
    getProfileData,
    updateProfileData,
  })

  const { loadWidgetsData, widgetsData } = useEmailProfileWidgetsData({
    enabled: withWidgets,
    getWidgetsData,
    isInitiated,
  })

  const { loadSummaryData, setSummaryWidgetData, summaryData } =
    useEmailProfileAISummaryData({
      getSummaryData: getSummaryDataApi ? loadAISummaryWidgetData : null,
      isInitiated,
    })

  const isMessagePending = isEmailMessagePending(entityData)

  async function loadRealtimeChannel() {
    return getEmailThreadProfile({
      params: { sub_menu: sub },
      uuid: entityData?.email_thread_uuid,
    }).then(({ data }) => {
      setRealtimeChannel(data.realtime_channel)
    })
  }

  useEffect(() => {
    if (isMessagePending && entityData?.email_thread_uuid) {
      loadRealtimeChannel()
    }
  }, [entityData?.email_thread_uuid, isMessagePending])

  function handleMessageCompleted(payload) {
    if (payload.uuid === mailUuid) {
      handleEntityReload()
    }
  }

  useEmailMessageCompletedRealtime({
    channel: realtimeChannel,
    enabled: isMessagePending,
    onInitialSubscribe: handleEntityReload,
    onMessageCompleted: handleMessageCompleted,
    onResync: handleEntityReload,
  })

  async function handleReloadProfile() {
    return handleEntityReload().then(() =>
      Promise.allSettled([
        withWidgets ? loadWidgetsData() : null,
        loadSummaryData(),
      ])
    )
  }

  return {
    entityData,
    handleEntityReload,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    loadAISummaryWidgetData,
    loadSummaryData,
    loadWidgetsData,
    serverError,
    setSummaryWidgetData,
    summaryData,
    widgetsData,
  }
}
